/**
 * Platform-ui chrome i18n catalogue.
 *
 * Site Vocabulary Phase 4a brings dashboard UI strings (Composer
 * placeholder, Thinking indicator, source panel headers, etc.) into
 * the same translation pipeline the widget uses. Super admins set
 * the English defaults + translations from Global Site Vocabulary;
 * tenants override individual strings on Site Vocabulary; the
 * dashboard reads the fully-resolved catalogue from /api/platform/me
 * and renders via useT().
 *
 * ## Why a separate catalogue from the widget
 *
 * The widget's catalogue is BAKED INTO THE BUNDLE (so the widget
 * has localised strings at first paint, before it talks to your
 * server). Platform-ui is a pure SPA, strings come from /me at
 * runtime. No bundle regeneration needed when a string changes.
 *
 * The two catalogues also have different key sets, widget has
 * "Send" / "Thinking…"; platform-ui has those plus dashboard chrome
 * (sidebar headings, modal titles, etc.). Keeping them separate
 * means evolving one doesn't bloat the other.
 *
 * ## Adding a string
 *
 * 1. Add the key (snake-cased, dot-grouped, e.g. 'composer.placeholder')
 *    to STRING_KEYS in services/platform_ui_i18n_defaults.py.
 * 2. Add the English default to that file's DEFAULTS['en'].
 * 3. Add the key here (in StringKey union) so TypeScript catches
 *    typos in t('…') calls.
 * 4. Refactor the component: replace the hardcoded string with t('key').
 *
 * The /me payload picks up the new key on the server's next deploy.
 * Super admins can then translate it via Global Site Vocabulary,
 * tenants can override via Site Vocabulary.
 */

// LangCode union, same 18 the widget supports. Kept as string
// literals so a typo in a dashboard component fails to compile.
export type LangCode =
  | 'en' | 'es' | 'fr' | 'de' | 'nl' | 'it' | 'pt' | 'ja'
  | 'ko' | 'zh' | 'ar' | 'hi' | 'ru' | 'pl' | 'tr' | 'sv'
  | 'th' | 'vi';

// Every key declared in services/platform_ui_i18n_defaults.STRING_KEYS.
// Hand-mirrored here (rather than auto-generated) because TypeScript
// needs the literal type union at compile time; the Python module
// is the runtime source of truth, this is the type façade.
export type StringKey =
  | 'composer.placeholder'
  | 'composer.send'
  | 'composer.send_title'
  | 'composer.dismiss'
  | 'composer.dismiss_error'
  | 'attach.tooltip'
  | 'attach.remove'
  | 'attach.dismiss_error'
  | 'attach.view_full_size'
  | 'attach.uploading'
  | 'attach.ready'
  | 'lightbox.title'
  | 'lightbox.close'
  | 'thinking.label'
  | 'thinking.generating'
  | 'thinking.found_in'
  | 'message.stop_reading'
  | 'message.stop_reading_aria'
  | 'message.stop_reading_title'
  | 'source_panel.close'
  | 'source_panel.contents'
  | 'video.open_fullscreen'
  | 'video.collapse'
  | 'conversation.loading'
  | 'conversation.load_failed'
  // Phase 4b-1: rest of chat surface
  | 'message.sources_label'
  | 'message.source_doc_default'
  | 'message.source_video_default'
  | 'message.source_view_doc_aria'
  | 'message.source_play_video_aria'
  | 'message.source_play_video_at_aria'
  | 'message.source_hide_video_aria'
  | 'message_attachments.alt'
  | 'message_attachments.view_aria'
  | 'mic.record'
  | 'mic.stop'
  | 'mic.transcribing'
  | 'audio_reply.stop_long'
  | 'audio_reply.turn_off'
  | 'audio_reply.turn_on'
  | 'audio_reply.stop_short'
  | 'audio_reply.label'
  // Phase 4b: dashboard/* chrome
  | 'common.close'
  | 'common.loading'
  | 'common.delete'
  | 'chat_placeholder.title'
  | 'chat_placeholder.subtitle'
  | 'client_switcher.default_strapline'
  | 'client_switcher.switch_label'
  | 'document_filter.default_group_label'
  | 'document_filter.all_documents'
  | 'document_filter.one_document_fallback'
  | 'document_filter.n_documents'
  | 'document_filter.trigger_title'
  | 'document_filter.filter_placeholder'
  | 'document_filter.filter_aria_label'
  | 'document_filter.documents_section_label'
  | 'document_filter.no_matches'
  | 'document_filter.clear_selection'
  | 'help_modal.title'
  | 'help_modal.lede'
  | 'help_modal.tip1_strong'
  | 'help_modal.tip1_body'
  | 'help_modal.tip2_strong'
  | 'help_modal.tip2_body'
  | 'help_modal.tip3_strong'
  | 'help_modal.tip3_body'
  | 'help_modal.tip4_strong'
  | 'help_modal.tip4_body'
  | 'help_modal.tip5_strong'
  | 'help_modal.tip5_body'
  | 'help_modal.tip6_strong'
  | 'help_modal.tip6_body'
  | 'help_modal.tip7_strong'
  | 'help_modal.tip7_body'
  | 'help_modal.tip8_strong'
  | 'help_modal.tip8_body'
  | 'language_picker.label'
  | 'language_picker.auto'
  | 'new_conversation.button_title'
  | 'new_conversation.resetting'
  | 'new_conversation.idle'
  | 'plan_modal.title'
  | 'plan_modal.error_load'
  | 'plan_modal.error_try_later'
  | 'plan_modal.price_per_month'
  | 'plan_modal.price_free'
  | 'plan_modal.cancels_on'
  | 'plan_modal.payment_failed_strong'
  | 'plan_modal.payment_failed_body'
  | 'plan_modal.usage_heading'
  | 'plan_modal.usage_unlimited'
  | 'plan_modal.usage_limit_suffix'
  | 'plan_modal.manage_link'
  | 'plan_modal.admin_hint'
  | 'plan_modal.non_admin_hint'
  | 'plan_modal.tier_name_fallback'
  | 'plan_modal.meter_documents_indexed'
  | 'plan_modal.meter_platform_users'
  | 'plan_modal.meter_widgets'
  | 'previous_conversations.loading'
  | 'previous_conversations.load_error'
  | 'previous_conversations.empty'
  | 'previous_conversations.delete_title'
  | 'previous_conversations.delete_message'
  | 'previous_conversations.empty_thread_title'
  | 'previous_conversations.truncate_empty'
  | 'previous_conversations.open_title'
  | 'previous_conversations.open_short_title'
  | 'previous_conversations.open_aria_label'
  | 'previous_conversations.delete_aria_label'
  | 'previous_conversations.message_count_one'
  | 'previous_conversations.message_count_other'
  | 'previous_conversations.time_just_now'
  | 'previous_conversations.time_minutes_ago'
  | 'previous_conversations.time_hours_ago'
  | 'previous_conversations.time_yesterday'
  | 'previous_conversations.time_days_ago'
  | 'rail.default_title'
  | 'rail.default_subtitle'
  | 'rail.admin_link_title'
  | 'rail.admin_link_label'
  | 'response_length.group_label'
  | 'response_length.standard'
  | 'response_length.detailed'
  | 'settings_modal.title'
  | 'settings_modal.appearance_heading'
  | 'settings_modal.appearance_light_label'
  | 'settings_modal.appearance_light_hint'
  | 'settings_modal.appearance_dark_label'
  | 'settings_modal.appearance_dark_hint'
  | 'settings_modal.appearance_system_label'
  | 'settings_modal.appearance_system_hint'
  | 'sidebar.brand_fallback'
  | 'sidebar.user_name_fallback'
  | 'sidebar.nav_aria_label'
  | 'sidebar.nav_ask'
  | 'sidebar.previous_conversations_label'
  | 'user_menu.trigger_aria_label'
  | 'user_menu.plan'
  | 'user_menu.settings'
  | 'user_menu.help'
  | 'user_menu.theme_light'
  | 'user_menu.theme_dark'
  | 'user_menu.log_out'
  // Phase 5 — bottom-of-message resources row
  | 'related_resources.label';

/** A bound translator: pre-resolved for a particular language.
 *  Returns the rendered string. */
export type Translator = (key: StringKey, vars?: Record<string, string>) => string;

/** Built-in English fallbacks. Mirrored from
 *  services/platform_ui_i18n_defaults.DEFAULTS['en']. If /me hasn't
 *  arrived yet (very first paint before the bootstrap fetch
 *  resolves), useT() renders these so the user doesn't see "key
 *  unknown" placeholders. */
export const BUILTIN_EN: Record<StringKey, string> = {
  'composer.placeholder':       'Ask a question…',
  'composer.send':              'Send',
  'composer.send_title':        'Send (Enter)',
  'composer.dismiss':           'Dismiss',
  'composer.dismiss_error':     'Dismiss error',
  'attach.tooltip':             'Attach an image',
  'attach.remove':              'Remove attachment',
  'attach.dismiss_error':       'Dismiss error',
  'attach.view_full_size':      'View full size',
  'attach.uploading':           'Uploading…',
  'attach.ready':               'Ready to send',
  'lightbox.title':             'Image preview',
  'lightbox.close':             'Close image preview',
  'thinking.label':             'Thinking',
  'thinking.generating':        'Generating a response',
  'thinking.found_in':          'Found an answer in ',
  'message.stop_reading':       'Stop reading',
  'message.stop_reading_aria':  'Stop reading this message aloud',
  'message.stop_reading_title': 'Stop reading this aloud',
  'source_panel.close':         'Close source panel',
  'source_panel.contents':      'Contents',
  'video.open_fullscreen':      'Open video in fullscreen',
  'video.collapse':             'Collapse video',
  'conversation.loading':       'Loading conversation…',
  'conversation.load_failed':   "Couldn't load your conversation.",
  // Phase 4b-1 additions
  'message.sources_label':            'Sources:',
  'message.source_doc_default':       'Document',
  'message.source_video_default':     'Video',
  'message.source_view_doc_aria':     'View {title}',
  'message.source_play_video_aria':   'Play video {title}',
  'message.source_play_video_at_aria': 'Play video {title} at {stamp}',
  'message.source_hide_video_aria':   'Hide video {title}',
  'message_attachments.alt':          'Attached image',
  'message_attachments.view_aria':    'View attachment full size',
  'mic.record':                       'Record voice question',
  'mic.stop':                         'Stop recording',
  'mic.transcribing':                 'Transcribing…',
  'audio_reply.stop_long':            'Stop audio reply',
  'audio_reply.turn_off':             'Turn audio replies off',
  'audio_reply.turn_on':              'Turn audio replies on',
  'audio_reply.stop_short':           'Stop audio',
  'audio_reply.label':                'Audio replies',
  // Phase 4b: dashboard/* chrome
  'common.close':                            'Close',
  'common.loading':                          'Loading…',
  'common.delete':                           'Delete',
  'chat_placeholder.title':                  'Ready when you are',
  'chat_placeholder.subtitle':               'Ask anything from your workspace documents. Type a question below or use the mic to speak.',
  'client_switcher.default_strapline':       'Your on-the-job mentor',
  'client_switcher.switch_label':            'Switch organisation',
  'document_filter.default_group_label':     'Topics',
  'document_filter.all_documents':           'All Documents',
  'document_filter.one_document_fallback':   '1 document',
  'document_filter.n_documents':             '{count} documents',
  'document_filter.trigger_title':           'Filter source documents',
  'document_filter.filter_placeholder':      'Filter…',
  'document_filter.filter_aria_label':       'Filter documents',
  'document_filter.documents_section_label': 'Documents',
  'document_filter.no_matches':              'No matches.',
  'document_filter.clear_selection':         'Clear selection',
  'help_modal.title':                        'Help',
  'help_modal.lede':                         'Your workplace support buddy.',
  'help_modal.tip1_strong':                  'Type or record',
  'help_modal.tip1_body':                    'your question in the composer. Hit the paper-plane to send.',
  'help_modal.tip2_strong':                  'Standard / Detailed',
  'help_modal.tip2_body':                    'on the rail controls reply length.',
  'help_modal.tip3_strong':                  'Documents',
  'help_modal.tip3_body':                    'filter restricts answers to a topic or specific files.',
  'help_modal.tip4_strong':                  'Auto / Language picker',
  'help_modal.tip4_body':                    'forces every reply into your chosen language.',
  'help_modal.tip5_strong':                  'Audio replies',
  'help_modal.tip5_body':                    'in the composer footer reads answers aloud.',
  'help_modal.tip6_strong':                  'New conversation',
  'help_modal.tip6_body':                    'in the sidebar starts a fresh thread.',
  'help_modal.tip7_strong':                  'History',
  'help_modal.tip7_body':                    'lets you jump back to any previous thread.',
  'help_modal.tip8_strong':                  'Click a source',
  'help_modal.tip8_body':                    'below an assistant message to open the document panel (when your tenant has citation view enabled).',
  'language_picker.label':                   'Reply language',
  'language_picker.auto':                    'Auto',
  'new_conversation.button_title':           'Start a new conversation',
  'new_conversation.resetting':              'Resetting…',
  'new_conversation.idle':                   'New conversation',
  'plan_modal.title':                        'Plan',
  'plan_modal.error_load':                   'Could not load your plan.',
  'plan_modal.error_try_later':              'Try again later.',
  'plan_modal.price_per_month':              '{price} / month',
  'plan_modal.price_free':                   'Free',
  'plan_modal.cancels_on':                   'Subscription cancels on {date}.',
  'plan_modal.payment_failed_strong':        'Payment failed.',
  'plan_modal.payment_failed_body':          ' Ask your admin to update the payment method or your access will pause soon.',
  'plan_modal.usage_heading':                'Usage',
  'plan_modal.usage_unlimited':              ' · Unlimited',
  'plan_modal.usage_limit_suffix':           ' / {limit}',
  'plan_modal.manage_link':                  'Manage subscription in admin',
  'plan_modal.admin_hint':                   'Open the admin app to switch plans, manage payment methods, and view invoices.',
  'plan_modal.non_admin_hint':               "To change plan, manage payment methods, or view invoices, contact one of your organisation's admins.",
  'plan_modal.tier_name_fallback':           'Plan',
  'plan_modal.meter_documents_indexed':      'Documents indexed',
  'plan_modal.meter_platform_users':         'Dashboard users',
  'plan_modal.meter_widgets':                'Widgets',
  'previous_conversations.loading':              'Loading conversations…',
  'previous_conversations.load_error':           "Couldn't load your conversations.",
  'previous_conversations.empty':                'No previous conversations yet.',
  'previous_conversations.delete_title':         'Delete conversation?',
  'previous_conversations.delete_message':       '"{title}" and all its messages will be permanently removed. This can\'t be undone.',
  'previous_conversations.empty_thread_title':   '(empty conversation)',
  'previous_conversations.truncate_empty':       '(empty)',
  'previous_conversations.open_title':           'Open this conversation',
  'previous_conversations.open_short_title':     'Open',
  'previous_conversations.open_aria_label':      'Open conversation: {title}',
  'previous_conversations.delete_aria_label':    'Delete conversation: {title}',
  'previous_conversations.message_count_one':    '1 message',
  'previous_conversations.message_count_other':  '{count} messages',
  'previous_conversations.time_just_now':        'just now',
  'previous_conversations.time_minutes_ago':     '{n}m ago',
  'previous_conversations.time_hours_ago':       '{n}h ago',
  'previous_conversations.time_yesterday':       'yesterday',
  'previous_conversations.time_days_ago':        '{n}d ago',
  'rail.default_title':                      'New conversation',
  'rail.default_subtitle':                   'Ask anything from your workspace documents',
  'rail.admin_link_title':                   'Open the admin portal',
  'rail.admin_link_label':                   'Admin portal',
  'response_length.group_label':             'Response length',
  'response_length.standard':                'Standard',
  'response_length.detailed':                'Detailed',
  'settings_modal.title':                    'Settings',
  'settings_modal.appearance_heading':       'Appearance',
  'settings_modal.appearance_light_label':   'Light',
  'settings_modal.appearance_light_hint':    'Always light theme.',
  'settings_modal.appearance_dark_label':    'Dark',
  'settings_modal.appearance_dark_hint':     'Always dark theme.',
  'settings_modal.appearance_system_label':  'Match system',
  'settings_modal.appearance_system_hint':   'Follow your OS appearance setting live.',
  'sidebar.brand_fallback':                  'Locksmith',
  'sidebar.user_name_fallback':              'Guest',
  'sidebar.nav_aria_label':                  'Main',
  'sidebar.nav_ask':                         'Ask',
  'sidebar.previous_conversations_label':    'Previous Conversations',
  'user_menu.trigger_aria_label':            'User menu',
  'user_menu.plan':                          'Plan',
  'user_menu.settings':                      'Settings',
  'user_menu.help':                          'Help',
  'user_menu.theme_light':                   'Light theme',
  'user_menu.theme_dark':                    'Dark theme',
  'user_menu.log_out':                       'Log out',
  'related_resources.label':                 'Related resources',
};

/** Shape of the /me payload's `platform_ui_i18n` field, fully
 *  resolved catalogue per language, sent every page load.
 *  Sparse keys are tolerated (resolver falls back to BUILTIN_EN). */
export type PlatformUiI18nPayload = Partial<
  Record<LangCode, Partial<Record<StringKey, string>>>
>;

/** Build a translator bound to a particular language. The runtime
 *  catalogue from /me wins; missing keys fall back to BUILTIN_EN
 *  so the dashboard renders something even on first paint or after
 *  a Firestore read failure. {placeholder} substitution is one-pass,
 *  no escaping, matches the widget buildTranslator. */
export function buildTranslator(
  lang: LangCode,
  catalogue: PlatformUiI18nPayload,
): Translator {
  const langBlock = catalogue[lang] || {};
  return (key, vars) => {
    let out = langBlock[key] ?? BUILTIN_EN[key] ?? String(key);
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        out = out.split(`{${k}}`).join(v);
      }
    }
    return out;
  };
}
